import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useNavigate,
  useParams,
  type RouterHistory,
} from "@tanstack/react-router";
import { lazy, Suspense, useEffect } from "react";
import { useActive } from "./state/hooks";
import { LoadingLine } from "./components/common";
import { JoinDeepLinkScreen, JoinScreen } from "./screens/JoinScreen";
import { IdentityScreen } from "./screens/IdentityScreen";
import { QuestsScreen } from "./screens/QuestsScreen";
import { QuestDetailScreen } from "./screens/QuestDetailScreen";
import { ResolveScreen } from "./screens/ResolveScreen";
import { PhotoQuestScreen } from "./screens/PhotoQuestScreen";
import { QuizScreen } from "./screens/QuizScreen";
import { LeaderboardScreen } from "./screens/LeaderboardScreen";
import { ShopScreen } from "./screens/ShopScreen";
import { OrderDetailScreen, OrdersScreen } from "./screens/OrdersScreen";
import { ProfileScreen } from "./screens/ProfileScreen";
import { ContactsScreen } from "./screens/ContactsScreen";
import { FeedbackStepperScreen } from "./screens/FeedbackStepperScreen";

// The camera/QR-decoder stack is heavy — load the scanner on demand.
const ScannerScreenLazy = lazy(() =>
  import("./screens/ScannerScreen").then((m) => ({ default: m.ScannerScreen })),
);

function ScannerRoute() {
  return (
    <Suspense fallback={<LoadingLine />}>
      <ScannerScreenLazy />
    </Suspense>
  );
}

type RouteParams = { code?: string; questId?: string; orderId?: string };

function useRouteParams(): RouteParams {
  return useParams({ strict: false }) as RouteParams;
}

/** `/` — joined players land on the quest home, everyone else on join. */
function IndexRedirect() {
  const navigate = useNavigate();
  const { active, isLoading } = useActive();
  useEffect(() => {
    if (isLoading) return;
    void navigate({ to: active ? "/quests" : "/join", replace: true });
  }, [isLoading, active, navigate]);
  return <LoadingLine />;
}

function JoinDeepLink() {
  const { code = "" } = useRouteParams();
  return <JoinDeepLinkScreen code={code} />;
}
function QuestDetail() {
  const { questId = "" } = useRouteParams();
  return <QuestDetailScreen questId={questId} />;
}
function ResolveQuest() {
  const { code = "" } = useRouteParams();
  return <ResolveScreen kind="q" code={code} />;
}
function ResolveContact() {
  const { code = "" } = useRouteParams();
  return <ResolveScreen kind="c" code={code} />;
}
function ResolveOrder() {
  const { code = "" } = useRouteParams();
  return <ResolveScreen kind="o" code={code} />;
}
function PhotoQuest() {
  const { questId = "" } = useRouteParams();
  return <PhotoQuestScreen questId={questId} />;
}
function Quiz() {
  const { questId = "" } = useRouteParams();
  return <QuizScreen questId={questId} />;
}
function FeedbackStepper() {
  const { questId = "" } = useRouteParams();
  return <FeedbackStepperScreen questId={questId} />;
}
function OrderDetail() {
  const { orderId = "" } = useRouteParams();
  return <OrderDetailScreen orderId={orderId} />;
}

const rootRoute = createRootRoute({ component: Outlet });

const routeTree = rootRoute.addChildren([
  createRoute({ getParentRoute: () => rootRoute, path: "/", component: IndexRedirect }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/join",
    // `resolve` carries a pending /q /c /o target scanned before joining.
    validateSearch: (search: Record<string, unknown>): { resolve?: string } =>
      typeof search.resolve === "string" && search.resolve ? { resolve: search.resolve } : {},
    component: JoinScreen,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/j/$code",
    // `resolve`: a quest QR scanned before joining led here (join, then solve it).
    validateSearch: (search: Record<string, unknown>): { resolve?: string } =>
      typeof search.resolve === "string" && search.resolve ? { resolve: search.resolve } : {},
    component: JoinDeepLink,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/identity", component: IdentityScreen }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/quests",
    validateSearch: (search: Record<string, unknown>): { tour?: number } =>
      search.tour ? { tour: Number(search.tour) } : {},
    component: QuestsScreen,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/quests/$questId", component: QuestDetail }),
  createRoute({ getParentRoute: () => rootRoute, path: "/scan", component: ScannerRoute }),
  createRoute({ getParentRoute: () => rootRoute, path: "/q/$code", component: ResolveQuest }),
  createRoute({ getParentRoute: () => rootRoute, path: "/c/$code", component: ResolveContact }),
  createRoute({ getParentRoute: () => rootRoute, path: "/o/$code", component: ResolveOrder }),
  createRoute({ getParentRoute: () => rootRoute, path: "/photo/$questId", component: PhotoQuest }),
  createRoute({ getParentRoute: () => rootRoute, path: "/quiz/$questId", component: Quiz }),
  createRoute({ getParentRoute: () => rootRoute, path: "/ranks", component: LeaderboardScreen }),
  createRoute({ getParentRoute: () => rootRoute, path: "/shop", component: ShopScreen }),
  createRoute({ getParentRoute: () => rootRoute, path: "/orders", component: OrdersScreen }),
  createRoute({ getParentRoute: () => rootRoute, path: "/orders/$orderId", component: OrderDetail }),
  createRoute({ getParentRoute: () => rootRoute, path: "/profile", component: ProfileScreen }),
  createRoute({ getParentRoute: () => rootRoute, path: "/contacts", component: ContactsScreen }),
  createRoute({ getParentRoute: () => rootRoute, path: "/feedback/$questId", component: FeedbackStepper }),
]);

export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    history,
    defaultPreload: false,
    scrollRestoration: true,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;
